import { useState, useMemo, useEffect } from 'react';
import { parsePhoneNumberFromString, AsYouType, CountryCode, getCountryCallingCode } from 'libphonenumber-js';
import styles from './FallbackLeadForm.module.css';
import { useConfigStore } from '@/store/configStore';
import { useToastStore } from '@/store/toastStore';
import { useChatStore } from '@/store/chatStore';
import { N8nApiService, getPageContext } from '@/services/api/n8n.api';
import { generateUUID } from '@/utils/uuid';
import { analytics } from '@/utils/analytics';
import { chatLogger } from '@/utils/chatLogger';
import type { Message } from '@/types/message.types';

// Флаги стран
const countryFlags: Record<string, string> = {
  RU: '🇷🇺', DE: '🇩🇪', GB: '🇬🇧', US: '🇺🇸', FR: '🇫🇷', IT: '🇮🇹', ES: '🇪🇸',
  AT: '🇦🇹', CH: '🇨🇭', PL: '🇵🇱', NL: '🇳🇱', BE: '🇧🇪', UA: '🇺🇦', BY: '🇧🇾',
  KZ: '🇰🇿', AE: '🇦🇪', TR: '🇹🇷', CN: '🇨🇳', JP: '🇯🇵', KR: '🇰🇷', IN: '🇮🇳',
};

// Определение страны по умолчанию по языку
const getDefaultCountry = (lang: string): CountryCode => {
  switch (lang) {
    case 'ru': return 'RU';
    case 'de': return 'DE';
    case 'en': return 'GB';
    default: return 'DE';
  }
};

const COUNTRY_TIMEZONE: Record<string, string> = {
  RU: 'Europe/Moscow', DE: 'Europe/Berlin', GB: 'Europe/London',
  US: 'America/New_York', FR: 'Europe/Paris', IT: 'Europe/Rome',
  ES: 'Europe/Madrid', AT: 'Europe/Vienna', CH: 'Europe/Zurich',
  PL: 'Europe/Warsaw', NL: 'Europe/Amsterdam', BE: 'Europe/Brussels',
  UA: 'Europe/Kyiv', BY: 'Europe/Minsk', KZ: 'Asia/Almaty',
  AE: 'Asia/Dubai', TR: 'Europe/Istanbul', CN: 'Asia/Shanghai',
  JP: 'Asia/Tokyo', KR: 'Asia/Seoul', IN: 'Asia/Kolkata',
};

function getUtcOffset(tz: string): number {
  try {
    const now = new Date();
    const parts = new Intl.DateTimeFormat('en-US', { timeZone: tz, timeZoneName: 'shortOffset' }).formatToParts(now);
    const offsetPart = parts.find(p => p.type === 'timeZoneName')?.value || '';
    const match = offsetPart.match(/GMT([+-]?\d+(?::(\d+))?)/);
    if (!match) return 0;
    const hours = parseInt(match[1], 10);
    const mins = match[2] ? parseInt(match[2], 10) : 0;
    return hours + (hours < 0 ? -mins : mins) / 60;
  } catch {
    return 0;
  }
}

function getTzLabel(tz: string): string {
  try {
    const parts = new Intl.DateTimeFormat('en-US', { timeZone: tz, timeZoneName: 'short' }).formatToParts(new Date());
    const name = parts.find(p => p.type === 'timeZoneName')?.value || tz;
    const offset = getUtcOffset(tz);
    const sign = offset >= 0 ? '+' : '';
    const h = Math.trunc(offset);
    const m = Math.abs(Math.round((offset - h) * 60));
    return `${name} (UTC${sign}${h}${m ? ':' + String(m).padStart(2, '0') : ''})`;
  } catch {
    return tz;
  }
}

const MSK_TZ = 'Europe/Moscow';

type TimeSlot = 'morning' | 'afternoon' | 'evening';
type ContactChannel = 'telegram' | 'whatsapp' | 'email' | 'phone';

// Время слотов (в местном времени клиента)
const TIME_SLOTS: Record<TimeSlot, { from: number; to: number }> = {
  morning: { from: 9, to: 12 },
  afternoon: { from: 12, to: 17 },
  evening: { from: 17, to: 20 },
};

// i18n лейблы
const LABELS: Record<string, Record<string, string>> = {
  name: { ru: 'Имя', en: 'Name', de: 'Name' },
  namePlaceholder: { ru: 'Ваше имя', en: 'Your name', de: 'Ihr Name' },
  phone: { ru: 'Телефон', en: 'Phone', de: 'Telefon' },
  phonePlaceholderRU: { ru: '+7 999 123 45 67', en: '+7 999 123 45 67', de: '+7 999 123 45 67' },
  phonePlaceholderDE: { ru: '+49 123 456 789', en: '+49 123 456 789', de: '+49 123 456 789' },
  phonePlaceholderEN: { ru: '+44 20 1234 5678', en: '+44 20 1234 5678', de: '+44 20 1234 5678' },
  enterFullNumber: { ru: 'Введите полный номер', en: 'Enter complete number', de: 'Vollständige Nummer eingeben' },
  invalidPhone: { ru: 'Введите корректный номер телефона', en: 'Please enter a valid phone number', de: 'Bitte geben Sie eine gültige Telefonnummer ein' },
  preferredTime: { ru: 'Удобное время для звонка', en: 'Preferred call time', de: 'Bevorzugte Anrufzeit' },
  morning: { ru: 'Утро', en: 'Morning', de: 'Morgens' },
  afternoon: { ru: 'День', en: 'Afternoon', de: 'Nachmittags' },
  evening: { ru: 'Вечер', en: 'Evening', de: 'Abends' },
  morningHours: { ru: '9:00–12:00', en: '9:00–12:00', de: '9:00–12:00' },
  afternoonHours: { ru: '12:00–17:00', en: '12:00–17:00', de: '12:00–17:00' },
  eveningHours: { ru: '17:00–20:00', en: '17:00–20:00', de: '17:00–20:00' },
  yourTimezone: { ru: 'Ваш часовой пояс', en: 'Your timezone', de: 'Ihre Zeitzone' },
  mskTime: { ru: 'по Москве', en: 'Moscow time', de: 'Moskauer Zeit' },
  contactChannel: { ru: 'Как с вами связаться?', en: 'How to contact you?', de: 'Wie können wir Sie erreichen?' },
  telegram: { ru: 'Telegram', en: 'Telegram', de: 'Telegram' },
  whatsapp: { ru: 'WhatsApp', en: 'WhatsApp', de: 'WhatsApp' },
  email: { ru: 'Email', en: 'Email', de: 'E-Mail' },
  phoneCall: { ru: 'Звонок', en: 'Call', de: 'Anruf' },
  telegramPlaceholder: { ru: '@username', en: '@username', de: '@Benutzername' },
  emailPlaceholder: { ru: 'email@example.com', en: 'email@example.com', de: 'email@example.com' },
  submit: { ru: 'Отправить', en: 'Send', de: 'Senden' },
  submitting: { ru: 'Отправляем...', en: 'Sending...', de: 'Senden...' },
  successSent: { ru: 'Отправлено', en: 'Sent', de: 'Gesendet' },
  errorSend: { ru: 'Не удалось отправить. Попробуйте позже.', en: 'Failed to send. Please try again later.', de: 'Senden fehlgeschlagen. Bitte versuchen Sie es später.' },
  confirmRU: { ru: 'Спасибо! Передали контакт, скоро свяжемся.', en: 'Thanks! We will contact you shortly.', de: 'Danke! Wir melden uns in Kürze.' },
};

const t = (key: string, lang: string): string => {
  return LABELS[key]?.[lang] || LABELS[key]?.en || key;
};

// Дефолтные fallback сообщения (при ошибке API)
const DEFAULT_FALLBACK: Record<string, string> = {
  ru: 'К сожалению, сейчас не могу ответить. Оставьте контакты — мы свяжемся.',
  en: 'Sorry, I cannot respond right now. Leave your contact and we will call you back.',
  de: 'Leider kann ich gerade nicht antworten. Hinterlassen Sie Ihre Kontaktdaten.',
};

// Текст при intent_detected (пользователь хочет связаться — позитивный тон)
const INTENT_LEAD_TEXT: Record<string, string> = {
  ru: 'Оставьте ваши контактные данные, и наш специалист свяжется с вами в ближайшее время.',
  en: 'Leave your contact details and our specialist will reach out to you shortly.',
  de: 'Hinterlassen Sie Ihre Kontaktdaten und unser Spezialist wird sich in Kürze bei Ihnen melden.',
};

function convertToMSK(hour: number, clientUtcOffset: number): number {
  const mskOffset = getUtcOffset(MSK_TZ);
  const utcHour = hour - clientUtcOffset;
  const mskHour = utcHour + mskOffset;
  return ((mskHour % 24) + 24) % 24;
}

/** Форматировать часы как "HH:00" */
function formatHour(h: number): string {
  return `${h}:00`;
}

interface FallbackLeadFormProps {
  reason?: string;
  hideIntro?: boolean;
}

export function FallbackLeadForm({ reason, hideIntro }: FallbackLeadFormProps) {
  const { config, language } = useConfigStore();
  
  const fallbackText = useMemo(() => {
    if (hideIntro) return null;
    if (reason === 'intent_detected') {
      return INTENT_LEAD_TEXT[language] || INTENT_LEAD_TEXT.en;
    }
    const fm = config.fallbackMessages;
    return (fm && (fm as any)[language]) || (fm as any)?.en || DEFAULT_FALLBACK[language] || DEFAULT_FALLBACK.en;
  }, [config.fallbackMessages, language, reason, hideIntro]);

  const { showError, showSuccess } = useToastStore();
  const { addMessage, userId, conversationId } = useChatStore();

  const defaultCountry = useMemo(() => getDefaultCountry(language), [language]);
  
  const [name, setName] = useState('');
  const [phoneRaw, setPhoneRaw] = useState('');
  const [detectedCountry, setDetectedCountry] = useState<CountryCode>(defaultCountry);
  const [selectedTime, setSelectedTime] = useState<TimeSlot | null>(null);
  const [selectedChannel, setSelectedChannel] = useState<ContactChannel | null>(null);
  const [telegramUsername, setTelegramUsername] = useState('');
  const [emailValue, setEmailValue] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!phoneRaw) {
      const code = getCountryCallingCode(defaultCountry);
      setPhoneRaw(`+${code} `);
    }
  }, [defaultCountry]);

  useEffect(() => {
    analytics.leadFormShown(reason);
  }, []);

  const handlePhoneChange = (value: string) => {
    const cleaned = value.replace(/[^\d+\s\-()]/g, '');
    const formatter = new AsYouType(detectedCountry);
    const formatted = formatter.input(cleaned);
    const country = formatter.getCountry();
    if (country) {
      setDetectedCountry(country);
    }
    setPhoneRaw(formatted || cleaned);
  };

  const phoneInfo = useMemo(() => {
    const p = parsePhoneNumberFromString(phoneRaw || '', detectedCountry);
    return p && p.isPossible() ? p : null;
  }, [phoneRaw, detectedCountry]);

  const countryFlag = countryFlags[detectedCountry] || '🌍';

  const clientTz = useMemo(() => {
    const tz = COUNTRY_TIMEZONE[detectedCountry] || 'UTC';
    return { tz, utcOffset: getUtcOffset(tz), label: getTzLabel(tz) };
  }, [detectedCountry]);

  const mskTimeLabel = useMemo(() => {
    if (!selectedTime) return null;
    const slot = TIME_SLOTS[selectedTime];
    const mskFrom = convertToMSK(slot.from, clientTz.utcOffset);
    const mskTo = convertToMSK(slot.to, clientTz.utcOffset);
    return `${formatHour(mskFrom)}–${formatHour(mskTo)} ${t('mskTime', language)}`;
  }, [selectedTime, clientTz.utcOffset, language]);

  const phonePlaceholder = useMemo(() => {
    if (language === 'ru' || detectedCountry === 'RU') return t('phonePlaceholderRU', language);
    if (language === 'de' || detectedCountry === 'DE') return t('phonePlaceholderDE', language);
    return t('phonePlaceholderEN', language);
  }, [language, detectedCountry]);

  const handleSubmit = async () => {
    const phone = phoneInfo?.number;
    if (!phone) {
      showError(t('invalidPhone', language));
      return;
    }
    setSubmitting(true);
    try {
      const service = new N8nApiService({
        baseURL: config.baseURL || window.location.origin,
        apiKey: config.apiKey,
        debug: config.debug,
      });
      
      const pageContext = getPageContext();
      
      await service.sendLeadCapture(
        {
          name: name.trim() || undefined,
          phone,
          email: selectedChannel === 'email' && emailValue.trim() ? emailValue.trim() : undefined,
          language,
          userId,
          conversationId: conversationId || undefined,
          context: pageContext,
          preferredContactTime: selectedTime || undefined,
          timezone: clientTz.tz,
          countryCode: detectedCountry,
          preferredChannel: selectedChannel || undefined,
          telegramUsername: selectedChannel === 'telegram' && telegramUsername.trim()
            ? telegramUsername.trim()
            : undefined,
        },
        config.callbackEndpoint || '/webhook/lead-capture'
      );

      analytics.leadFormSubmitted(selectedChannel || undefined);

      const confirmMessage: Message = {
        id: generateUUID(),
        content: t('confirmRU', language),
        sender: 'assistant',
        timestamp: Date.now(),
      };
      addMessage(confirmMessage);
      
      // Reset form
      setName('');
      setPhoneRaw('');
      setSelectedTime(null);
      setSelectedChannel(null);
      setTelegramUsername('');
      setEmailValue('');
      showSuccess(t('successSent', language));
    } catch (e: any) {
      showError(t('errorSend', language));
      chatLogger.error(e);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={styles.wrapper}>
      {fallbackText && <div className={styles.text}>{fallbackText}</div>}
      <div className={styles.form}>
        {/* Имя */}
        <label className={styles.label}>
          {t('name', language)}
          <input
            className={styles.input}
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t('namePlaceholder', language)}
          />
        </label>

        {/* Телефон */}
        <label className={styles.label}>
          {t('phone', language)}
          <div className={styles.phoneInputWrapper}>
            <span className={styles.countryFlag}>{countryFlag}</span>
            <input
              className={styles.phoneInput}
              value={phoneRaw}
              onChange={(e) => handlePhoneChange(e.target.value)}
              placeholder={phonePlaceholder}
              type="tel"
              autoComplete="tel"
            />
          </div>
          {phoneInfo && (
            <div className={styles.helper}>
              ✓ {phoneInfo.formatInternational()}
            </div>
          )}
          {phoneRaw.length > 4 && !phoneInfo && (
            <div className={styles.errorHint}>
              {t('enterFullNumber', language)}
            </div>
          )}
        </label>

        {/* Канал связи */}
        <div className={styles.fieldGroup}>
          <span className={styles.fieldLabel}>{t('contactChannel', language)}</span>
          <div className={styles.channelSelector}>
            {(['telegram', 'whatsapp', 'email', 'phone'] as ContactChannel[]).map((ch) => (
              <button
                key={ch}
                type="button"
                className={`${styles.channelChip} ${selectedChannel === ch ? styles.channelChipActive : ''}`}
                onClick={() => setSelectedChannel(selectedChannel === ch ? null : ch)}
              >
                <span className={styles.channelIcon}>
                  {ch === 'telegram' && '✈'}
                  {ch === 'whatsapp' && '💬'}
                  {ch === 'email' && '✉'}
                  {ch === 'phone' && '📞'}
                </span>
                {t(ch === 'phone' ? 'phoneCall' : ch, language)}
              </button>
            ))}
          </div>

          {/* Условное поле: Telegram username */}
          {selectedChannel === 'telegram' && (
            <input
              className={styles.conditionalInput}
              value={telegramUsername}
              onChange={(e) => setTelegramUsername(e.target.value)}
              placeholder={t('telegramPlaceholder', language)}
              autoComplete="off"
            />
          )}

          {/* Условное поле: Email */}
          {selectedChannel === 'email' && (
            <input
              className={styles.conditionalInput}
              value={emailValue}
              onChange={(e) => setEmailValue(e.target.value)}
              placeholder={t('emailPlaceholder', language)}
              type="email"
              autoComplete="email"
            />
          )}
        </div>

        {/* Удобное время */}
        <div className={styles.fieldGroup}>
          <span className={styles.fieldLabel}>{t('preferredTime', language)}</span>
          
          {/* Таймзона клиента */}
          <div className={styles.timezoneInfo}>
            {countryFlag} {t('yourTimezone', language)}: {clientTz.label}
          </div>

          <div className={styles.timeSelector}>
            {(['morning', 'afternoon', 'evening'] as TimeSlot[]).map((slot) => (
              <button
                key={slot}
                type="button"
                className={`${styles.timeSlot} ${selectedTime === slot ? styles.timeSlotActive : ''}`}
                onClick={() => setSelectedTime(selectedTime === slot ? null : slot)}
              >
                <span className={styles.timeSlotLabel}>{t(slot, language)}</span>
                <span className={styles.timeSlotHours}>{t(`${slot}Hours`, language)}</span>
              </button>
            ))}
          </div>

          {/* MSK конвертация */}
          {mskTimeLabel && (
            <div className={styles.mskConversion}>
              ≈ {mskTimeLabel}
            </div>
          )}
        </div>

        {/* Кнопка отправки */}
        <button
          className={styles.button}
          onClick={handleSubmit}
          disabled={submitting}
          type="button"
        >
          {submitting ? t('submitting', language) : t('submit', language)}
        </button>
      </div>
    </div>
  );
}
